import React, { useState, useEffect } from 'react';
import { useCart } from '../contexts/CartContext';
import { FiX, FiPlus, FiMinus, FiShoppingCart } from 'react-icons/fi';
import { FaCopy, FaCheck } from 'react-icons/fa';
import logo from '/summiit-logo.png';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DirectPaystackButton from './DirectPaystackButton';
import NetworkStatus from './NetworkStatus';
import { API_SETTINGS, getApiUrl } from '../config/apiSettings';
import apiService from '../services/apiService';

const CartDrawer = () => {
  const { 
    cartItems, 
    isCartOpen, 
    setIsCartOpen, 
    removeFromCart, 
    updateQuantity, 
    cartTotal,
    applicationReference,
    userInfo: contextUserInfo 
  } = useCart();
  const [userInfo, setUserInfo] = useState({
    name: '',
    email: ''
  });
  const [itemReferences, setItemReferences] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [paymentReference, setPaymentReference] = useState('');
  const [error, setError] = useState('');
  const [apiFailure, setApiFailure] = useState(false);
  const [userCountry, setUserCountry] = useState('NG'); // Default to Nigeria
  const navigate = useNavigate();

  // Constants
  const USD_TO_NGN_RATE = 1690;
  const API_URL = API_SETTINGS.API_URL;
  
  // Calculate totals
  const discount = promoApplied ? cartTotal * 0.1 : 0; // 10% discount
  const totalAmount = cartTotal - discount;
  const totalAmountInNaira = totalAmount * USD_TO_NGN_RATE;

  useEffect(() => {
    // Generate a unique payment reference
    const prefix = 'AFFF';
    const timestamp = Date.now().toString();
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const reference = `${prefix}-${timestamp}-${random}`;
    setPaymentReference(reference);
    
    // Load user info from context first, then localStorage as fallback
    if (contextUserInfo && (contextUserInfo.name || contextUserInfo.email)) {
      setUserInfo(prevInfo => ({
        ...prevInfo,
        name: contextUserInfo.name || prevInfo.name,
        email: contextUserInfo.email || prevInfo.email
      }));
    } else {
      // Fallback to localStorage
      const savedUserInfo = localStorage.getItem('userInfo');
      if (savedUserInfo) {
        const parsedInfo = JSON.parse(savedUserInfo);
        setUserInfo({
          name: parsedInfo.name || '',
          email: parsedInfo.email || ''
        });
      } else {
        // Try the financier-specific user info
        const financierUserInfo = localStorage.getItem('financierUserInfo');
        if (financierUserInfo) {
          const parsedInfo = JSON.parse(financierUserInfo);
          setUserInfo({
            name: parsedInfo.name || '',
            email: parsedInfo.email || ''
          });
        }
      }
    }
    
    // Initialize reference codes for each item
    const newItemRefs = {};
    cartItems.forEach(item => {
      // If the item has a reference code, use it
      if (item.referenceCode) {
        newItemRefs[item.uniqueId] = item.referenceCode;
      } else if (applicationReference) {
        // Use the application reference from context if available
        newItemRefs[item.uniqueId] = applicationReference;
      } else {
        // Otherwise check if we have a stored reference code
        const storedRef = sessionStorage.getItem('financierReferenceCode');
        if (storedRef) {
          newItemRefs[item.uniqueId] = storedRef;
        }
      }
    });
    
    // Only update if we have new references
    if (Object.keys(newItemRefs).length > 0) {
      setItemReferences(prev => ({
        ...prev,
        ...newItemRefs
      }));
    }
  }, [cartItems, applicationReference, contextUserInfo]);

  useEffect(() => {
    // Attempt to get user's location
    const getLocation = async () => {
      try {
        // Use ipinfo.io to get location without requiring permissions
        const response = await axios.get('https://ipinfo.io/json?token=ce8b6fd3f9a6c9');
        if (response.data && response.data.country) {
          setUserCountry(response.data.country);
          localStorage.setItem('userCountry', response.data.country);
          console.log("User country detected:", response.data.country);
        }
      } catch (error) {
        console.error("Error detecting location:", error);
        // Default to Nigeria if detection fails
        setUserCountry('NG');
      }
    };
    
    getLocation();
  }, []);

  const handleItemReferenceChange = (itemId, value) => {
    setItemReferences({
      ...itemReferences,
      [itemId]: value
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedUserInfo = { ...userInfo, [name]: value };
    setUserInfo(updatedUserInfo);
    localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));
  };

  const handleApplyPromo = (e) => {
    e.preventDefault();
    if (promoCode.toLowerCase() === 'afff10') {
      setPromoApplied(true);
      alert('Promo code applied successfully! 10% discount added.');
    } else {
      alert('Invalid promo code. Please try again.');
    }
  };

  // Add this function BEFORE it's used in the component
  const generateReference = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 10; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // Add this function to collect badge references
  const collectBadgeReferences = () => {
    return Object.entries(itemReferences).map(([id, ref]) => {
      const item = cartItems.find(item => item.uniqueId === id);
      return {
        badgeName: item?.name || 'Unknown Badge',
        reference: ref || 'No reference',
        price: item?.price || 0
      };
    });
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    
    if (!userInfo.email || !userInfo.name) {
      alert("Please fill in all required fields.");
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      console.log("Starting checkout process...");
      
      // Collect badge references
      const badgeReferences = collectBadgeReferences();
      console.log("Badge references:", badgeReferences);
      
      // Generate a simple alphanumeric reference
      const paymentReference = generateReference();
      console.log("Generated payment reference:", paymentReference);

      // IMPORTANT: Convert amount to kobo (multiply by 100)
      const amountInKobo = Math.round(totalAmountInNaira * 100);
      
      // Prepare payment data
      const paymentData = {
        email: userInfo.email,
        amount: amountInKobo, // Fixed: Amount in kobo
        currency: 'NGN',
        reference: paymentReference,
        paymentType: 'badge',
        customerName: userInfo.name, // Explicitly include customer name
        additionalData: {
          name: userInfo.name,
          email: userInfo.email,
          cartItems: JSON.stringify(cartItems),
          products: cartItems.map(item => `${item.name} (x${item.quantity || 1})`).join(', '), // Add product names
          badgeReferences: JSON.stringify(badgeReferences), // Include badge references
          discount: discount,
          totalAmount: totalAmount,
          originalCurrency: 'USD',
          amountInNaira: totalAmountInNaira, // Store original Naira amount before kobo conversion
          badgeReferenceList: badgeReferences.map(badge => badge.reference).join(', '),
          badgeNameList: badgeReferences.map(badge => badge.badgeName).join(', '),
          userCountry: userCountry
        }
      };
      
      console.log("Payment data:", paymentData);
      
      // Use apiService instead of direct axios call
      const response = await apiService.payments.initiate(paymentData);
      
      console.log("Payment initialization response:", response.data);
      
      if (response.data.status === 'success' && response.data.redirect_url) {
        // Save transaction information to localStorage for verification page
        localStorage.setItem('paymentReference', paymentReference);
        localStorage.setItem('paymentAmount', totalAmount.toString());
        localStorage.setItem('paymentCurrency', 'USD');
        localStorage.setItem('customerName', userInfo.name);
        localStorage.setItem('customerEmail', userInfo.email);
        
        // Save item references to localStorage
        localStorage.setItem('itemReferences', JSON.stringify(itemReferences));
        
        // Redirect to Paystack checkout URL
        window.location.href = response.data.redirect_url;
      } else {
        throw new Error('Failed to initialize payment. Invalid response from payment provider.');
      }
    } catch (error) {
      console.error('Payment initialization failed:', error);
      
      // More detailed error message
      let errorMessage = 'Payment could not be processed. Please try again.';
      
      if (error.response) {
        console.error('Error response data:', error.response.data);
        errorMessage = error.response.data?.message || 
                      error.response.data?.error || 
                      'Server error. Please try again later.';
      } else if (error.request) {
        console.error('No response received:', error.request);
        errorMessage = 'No response from server. Please check your internet connection.';
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleCartOpen = () => {
    setIsCartOpen(!isCartOpen);
  };

  const handleRemoveBadge = (badge) => {
    removeFromCart(badge);
  };

  return (
    <>
      {/* Cart Button - Always visible */}
      <div className="fixed bottom-6 right-6 z-[1000]">
        <button 
          onClick={() => setIsCartOpen(true)}
          className="bg-[#E76F51] text-white p-3 rounded-full shadow-lg hover:bg-[#E85C35] transition-colors"
        >
          <FiShoppingCart size={24} />
          {cartItems.length > 0 && (
            <span className="absolute -top-2 -right-2 bg-[#f0e1bb] text-[#002730] rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
              {cartItems.reduce((total, item) => total + (item.quantity || 1), 0)}
            </span>
          )}
        </button>
      </div>

      {/* Overlay */}
      {isCartOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-[1001]"
            onClick={() => setIsCartOpen(false)}
          ></div>
          
          <div className="fixed top-0 right-0 h-full w-full sm:w-[400px] md:w-[450px] bg-[#002730] shadow-xl z-[1002] font-outfit text-white overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-[#1A4450]">
              <div className="flex items-center">
                <img src={logo} alt="AFFF Logo" className="h-8 mr-2" />
                <h2 className="text-custom-brown text-2xl md:text-3xl lg:text-4xl font-header tracking-wide">YOUR AFFF SUMMIT BADGES</h2>
              </div>
              <button 
                onClick={() => setIsCartOpen(false)}
                className="text-gray-400 hover:text-white"
              >
                <FiX size={18} />
              </button>
            </div>
            
            <div className="flex-1 flex flex-col">
              {cartItems.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                  <FiShoppingCart size={48} className="text-gray-400 mb-4" />
                  <p className="text-sm mb-2">Your cart is empty</p>
                  <p className="text-xs text-gray-400 mb-4">Add badges to get started</p>
                  <button
                    onClick={() => setIsCartOpen(false)}
                    className="px-4 py-2 bg-[#E76F51] rounded text-sm font-medium hover:bg-[#E85C35]"
                  >
                    Explore Badges
                  </button>
                </div>
              ) : (
                <div className="flex flex-col h-full">
                  {/* Badge Items */}
                  <div style={{height: "60vh"}} className="overflow-y-auto custom-scrollbar">
                    <div className="p-4">
                      <h3 className="text-lg font-semibold text-[#afaa9b] mb-4">Selected Badges</h3>
                      
                      {cartItems.map((item, index) => (
                        <div 
                          key={item.uniqueId || index} 
                          className="mb-4 p-3 rounded-lg bg-[#1A4450] border border-[#336673]"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h3 className="text-sm font-medium">{item.name}</h3>
                              <p className="text-xs text-gray-300">{item.details}</p>
                            </div>
                            <button 
                              onClick={() => handleRemoveBadge(item)}
                              className="text-gray-400 hover:text-white"
                            >
                              <FiX size={18} />
                            </button>
                          </div>
                          <div className="flex justify-between items-center mb-2">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => updateQuantity(item, -1)}
                                className="bg-[#0D3640] hover:bg-[#0A2830] rounded-full p-1"
                                disabled={item.quantity <= 1}
                              >
                                <FiMinus size={14} />
                              </button>
                              <span className="text-sm">{item.quantity || 1}</span>
                              <button
                                onClick={() => updateQuantity(item, 1)}
                                className="bg-[#0D3640] hover:bg-[#0A2830] rounded-full p-1"
                              >
                                <FiPlus size={14} />
                              </button>
                            </div>
                            <span className="text-sm">₦{((item.price * (item.quantity || 1)) * USD_TO_NGN_RATE).toLocaleString()}</span>
                          </div>
                          <div className="mt-2">
                            <label className="block text-xs text-gray-400 mb-1">
                              Reference Code Or Enter Company/Participant Name - {applicationReference ? '(Auto-filled)' : ''}
                            </label>
                            <input
                              type="text"
                              value={itemReferences[item.uniqueId] || ''}
                              onChange={(e) => handleItemReferenceChange(item.uniqueId, e.target.value)}
                              className={`w-full p-1 text-sm bg-[#0A323C] border border-[#265361] rounded ${
                                applicationReference ? 'opacity-75' : ''
                              }`}
                              placeholder="Enter reference code"
                              readOnly={!!applicationReference}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Order Summary and Billing - Fixed height */}
                  <div style={{height: "60%"}} className="bg-[#0D3640] p-4 border-t border-[#1A4450] flex flex-col overflow-y-auto custom-scrollbar">
                    {/* Order Summary Section - Tighter spacing */}
                    <div className="mb-1">
                      <div className="flex justify-between text-sm mb-2">
                        <h3 className="text-[#f0e1bb]" style={{fontSize: '16px'}}>Order Summary</h3>
                        
                        {/* Promo Code Inline */}
                        <form onSubmit={handleApplyPromo} className="flex items-center">
                          <input
                            type="text"
                            placeholder="Promo code"
                            value={promoCode}
                            onChange={(e) => setPromoCode(e.target.value)}
                            className="bg-[#002730] border border-[#336673] rounded-l p-1.5 text-sm w-24"
                          />
                          <button
                            type="submit"
                            className="bg-[#E76F51] text-white px-3 py-1.5 rounded-r text-sm"
                          >
                            Apply
                          </button>
                        </form>
                      </div>
                      
                      {promoApplied && (
                        <div className="flex justify-between mb-1 text-green-400 text-sm">
                          <span>Discount:</span>
                          <span>-₦{(discount * USD_TO_NGN_RATE).toLocaleString()}</span>
                        </div>
                      )}
                      
                      <div className="flex justify-between text-white text-lg">
                        <span>Total:</span>
                        <span>₦{totalAmountInNaira.toLocaleString()}</span>
                      </div>
                    </div>
                    
                    {/* Divider line to visually connect sections */}
                    <div className="border-t border-[#1A4450] my-3"></div>
                    
                    {/* Billing Information - Moved up */}
                    <div>
                      <h3 className="text-[#f0e1bb] mb-2" style={{fontSize: '16px'}}>Billing information</h3>
                      <form onSubmit={handleCheckout} className="space-y-3">
                        <NetworkStatus />
                        <div>
                          <input
                            type="text"
                            name="name"
                            id="name"
                            required
                            placeholder="Full Name *"
                            value={userInfo.name}
                            onChange={handleInputChange}
                            className="w-full bg-[#1A4450] border border-[#336673] rounded p-2 text-sm"
                          />
                        </div>
                        
                        <div>
                          <input
                            type="email"
                            name="email"
                            id="email"
                            required
                            placeholder="Email Address *"
                            value={userInfo.email}
                            onChange={handleInputChange}
                            className="w-full bg-[#1A4450] border border-[#336673] rounded p-2 text-sm"
                          />
                        </div>
                        
                        {error && (
                          <div className="p-2 bg-red-900 border border-red-700 text-white rounded text-xs">
                            {error}
                          </div>
                        )}
                        
                        <div className="mt-4">
                          <button
                            type="submit"
                            className="w-full py-2 bg-[#E76F51] text-white rounded font-medium hover:bg-[#E85C35] transition-colors"
                            disabled={isLoading}
                          >
                            {isLoading ? 'Processing...' : 'Proceed to Payment'}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                  
                  {/* Explore Options Link */}
                  <div className="p-3 text-center bg-[#0D3640] border-t border-[#1A4450]">
                    <a href="/register" className="text-[#f0e1bb] hover:underline flex items-center justify-center text-sm">
                      Explore Badge Options <span className="ml-1">›</span>
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default CartDrawer; 